import { useEffect, useRef, useState } from 'react';
import { Music, VolumeX, Volume2 } from 'lucide-react';

const TRACK_URL = '/audio/lobby_lounge.mp3';
const STORAGE_KEY = 'lobby_music_v1';

/**
 * Floating lounge-music control for the Lobby page.
 * - Default OFF (browsers block autoplay anyway)
 * - One click → starts looping a public-domain 1920s lounge jazz track
 * - Volume slider on hover
 * - Persists preference + volume in localStorage
 * - Auto-pauses if player navigates away
 */
export const LobbyMusic = () => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(0.35);
    const [hover, setHover] = useState(false);

    // Restore saved preferences
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
            if (typeof saved.volume === 'number') setVolume(saved.volume);
            // We do NOT auto-restore "playing" — let the user click to acknowledge sound.
        } catch { /* ignore */ }
    }, []);

    // Apply volume to audio element
    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume }));
        } catch { /* ignore */ }
    }, [volume]);

    // Pause on unmount (leaving lobby)
    useEffect(() => {
        const audio = audioRef.current;
        return () => {
            if (audio) audio.pause();
        };
    }, []);

    const toggle = async () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
            setPlaying(false);
        } else {
            try {
                await audio.play();
                setPlaying(true);
            } catch (e) {
                // Autoplay blocked or network error — surface gracefully
                console.warn('Lobby music play failed:', e);
            }
        }
    };

    return (
        <div
            className="fixed bottom-6 left-6 z-40"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="lobby-music-widget"
        >
            <audio ref={audioRef} src={TRACK_URL} loop preload="none" />

            <div
                className="flex items-center gap-2 rounded-full pl-2 pr-3 py-2 backdrop-blur-md transition-all duration-300"
                style={{
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.18) 0%, rgba(0,0,0,0.7) 100%)',
                    border: `1px solid ${playing ? 'rgba(255,215,0,0.55)' : 'rgba(255,255,255,0.12)'}`,
                    boxShadow: playing ? '0 0 18px rgba(255,215,0,0.25)' : '0 4px 14px rgba(0,0,0,0.4)',
                }}
            >
                <button
                    onClick={toggle}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                    style={{
                        background: playing
                            ? 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)'
                            : 'rgba(255,255,255,0.06)',
                        color: playing ? '#000' : '#FFD700',
                    }}
                    aria-label={playing ? 'Pause lobby music' : 'Play lobby music'}
                    data-testid="lobby-music-toggle"
                >
                    {playing
                        ? <Music className="w-4 h-4" />
                        : <VolumeX className="w-4 h-4" />}
                </button>

                <div className="flex flex-col">
                    <span className="text-[10px] font-black tracking-wider text-[#FFD700] leading-tight">
                        LOUNGE
                    </span>
                    <span className="text-[9px] text-[#A0AAB2] leading-tight">
                        {playing ? 'now playing' : 'click to play'}
                    </span>
                </div>

                {/* Volume slider — appears on hover */}
                <div
                    className={`flex items-center overflow-hidden transition-all duration-300 ${
                        hover ? 'w-24 ml-2' : 'w-0 ml-0'
                    }`}
                >
                    <Volume2 className="w-3 h-3 text-[#A0AAB2] flex-shrink-0 mr-1" />
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={volume}
                        onChange={(e) => setVolume(parseFloat(e.target.value))}
                        className="lobby-music-slider w-full"
                        data-testid="lobby-music-volume"
                        aria-label="Lobby music volume"
                    />
                </div>
            </div>

            <style>{`
                .lobby-music-slider {
                    -webkit-appearance: none;
                    appearance: none;
                    height: 3px;
                    background: rgba(255,255,255,0.15);
                    border-radius: 9999px;
                    outline: none;
                    cursor: pointer;
                }
                .lobby-music-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 12px; height: 12px;
                    background: #FFD700;
                    border-radius: 50%;
                    cursor: pointer;
                    box-shadow: 0 0 6px rgba(255,215,0,0.6);
                }
                .lobby-music-slider::-moz-range-thumb {
                    width: 12px; height: 12px;
                    background: #FFD700;
                    border-radius: 50%;
                    cursor: pointer;
                    border: none;
                    box-shadow: 0 0 6px rgba(255,215,0,0.6);
                }
            `}</style>
        </div>
    );
};

export default LobbyMusic;
